import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { BaseScene } from './base-scene.js';
import { getStarterOptions, buildStarterMonster } from '../world/starters.js';
import { netManager } from '../net/index.js';
import { SKIN_KEYS } from '../world/characters/skins.js';
import { applyAccountToDataManager } from '../world/profile.js';

// ── Asset path helpers (mirror the load paths in preload-scene.js). ──
// Skin sheets: assets/images/mpwsp/character-overworld/ow{N}.png — a 4col x 4row 32px grid.
// The idle-down frame is the top-left 32x32 cell (background-position 0 0).
const SKIN_IMG = (i) => `assets/images/mpwsp/character-overworld/ow${i + 1}.png`;

// Catchimon battler sprites, keyed by the monsters.json `assetKey` of each starter so the
// card art stays correct if the roster mapping changes. Mirrors preload-scene.js load calls.
const CATCHIMON_IMG = Object.freeze({
  CATCHIMON_FIRE_01: 'assets/images/catchimon/creatures/fire_01_front.png',
  CATCHIMON_FIRE_02: 'assets/images/catchimon/creatures/fire_02_front.png',
  CATCHIMON_WATER_11: 'assets/images/catchimon/creatures/water_11_front.png',
  CATCHIMON_WATER_12: 'assets/images/catchimon/creatures/water_12_front.png',
  CATCHIMON_WATER_13: 'assets/images/catchimon/creatures/water_13_front.png',
  CATCHIMON_GRASS_19: 'assets/images/catchimon/creatures/grass_19_front.png',
  CATCHIMON_GRASS_20: 'assets/images/catchimon/creatures/grass_20_front.png',
});

const ELEMENT_COLOR = Object.freeze({ flame: '#ff7a59', tide: '#59c6ff', verdant: '#7fe08a' });
const OVERLAY_ID = 'profile-setup-overlay';
const STYLE_ID = 'profile-setup-overlay-style';

/**
 * New-player account setup — a single full-screen, responsive HTML/CSS DOM overlay mounted over
 * the Phaser canvas (the scene itself stays thin). Three zones — avatar picker, identity
 * (username + bio), and starter cards — that reflow from a 3-column desktop layout to a single
 * stacked, scrollable column below 900px so they never overlap. Fully mouse/touch-driven.
 *
 * The data flow (seed starter into the party, set skin/name/bio, createAccount, enter the world)
 * is preserved exactly from the original Phaser-drawn version.
 */
export class ProfileSetupScene extends BaseScene {
  /** @type {import('../world/starters.js').StarterOption[]} */ #options;
  /** @type {number} */ #selectedStarter;
  /** @type {number} */ #skinIndex;
  /** @type {HTMLDivElement | undefined} */ #overlay;
  /** @type {HTMLInputElement | undefined} */ #nameInput;
  /** @type {HTMLTextAreaElement | undefined} */ #bioInput;
  /** @type {boolean} */ #confirming;

  constructor() {
    super({ key: SCENE_KEYS.PROFILE_SETUP_SCENE });
  }

  init() {
    super.init();
    this.#options = getStarterOptions();
    this.#selectedStarter = 0;
    this.#skinIndex = 0;
    this.#confirming = false;
  }

  create() {
    super.create();
    this.#mountOverlay();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.#destroyOverlay());
  }

  // ── DOM overlay ──────────────────────────────────────────────────────────────────────────

  #mountOverlay() {
    this.#injectStyle();

    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'ps-overlay';
    overlay.innerHTML = this.#overlayHtml();
    document.body.appendChild(overlay);
    this.#overlay = overlay;

    this.#nameInput = /** @type {HTMLInputElement} */ (overlay.querySelector('#ps-name'));
    this.#bioInput = /** @type {HTMLTextAreaElement} */ (overlay.querySelector('#ps-bio'));
    if (this.#nameInput) this.#nameInput.value = this.#defaultName();

    this.#wireEvents(overlay);
    this.#renderAvatar();
    this.#renderStarters();

    // fade the whole overlay in
    requestAnimationFrame(() => overlay.classList.add('ps-in'));
  }

  #overlayHtml() {
    const skinDots = SKIN_KEYS.map((_, i) => `<span class="ps-dot" data-skin-dot="${i}"></span>`).join('');
    const starterCards = this.#options
      .map((option, index) => {
        const color = ELEMENT_COLOR[option.element] ?? '#ffffff';
        const monster = buildStarterMonster(this, option.speciesId);
        const img = monster ? CATCHIMON_IMG[monster.assetKey] : undefined;
        const sprite = img
          ? `<img class="ps-starter-sprite" src="${img}" alt="${option.name}" draggable="false" />`
          : `<div class="ps-starter-sprite ps-starter-sprite--fallback" style="color:${color}">?</div>`;
        return `
          <button type="button" class="ps-starter" data-starter="${index}" style="--elem:${color}">
            <span class="ps-starter-art">${sprite}</span>
            <span class="ps-starter-meta">
              <span class="ps-starter-name">${option.name}</span>
              <span class="ps-starter-elem" style="color:${color}">${option.element.toUpperCase()}</span>
            </span>
            <span class="ps-gem" style="background:${color}"></span>
          </button>`;
      })
      .join('');

    return `
      <div class="ps-bg" aria-hidden="true"></div>
      <div class="ps-scroll">
        <div class="ps-shell">
          <header class="ps-head">
            <p class="ps-eyebrow">CREATE YOUR</p>
            <h1 class="ps-title">HUNTER</h1>
            <span class="ps-divider"></span>
            <p class="ps-subtitle">One shared world. Set up your account, then dive in.</p>
          </header>

          <div class="ps-grid">
            <section class="ps-card ps-avatar">
              <span class="ps-corner ps-corner--tl"></span>
              <span class="ps-corner ps-corner--tr"></span>
              <span class="ps-corner ps-corner--bl"></span>
              <span class="ps-corner ps-corner--br"></span>
              <p class="ps-label">AVATAR</p>
              <div class="ps-avatar-stage">
                <button type="button" class="ps-arrow" data-avatar="-1" aria-label="Previous avatar">&#9664;</button>
                <div class="ps-avatar-frame">
                  <div class="ps-avatar-sprite" id="ps-avatar-sprite"></div>
                  <div class="ps-podium"></div>
                </div>
                <button type="button" class="ps-arrow" data-avatar="1" aria-label="Next avatar">&#9654;</button>
              </div>
              <div class="ps-dots">${skinDots}</div>
            </section>

            <section class="ps-card ps-identity">
              <p class="ps-label">IDENTITY</p>
              <label class="ps-field">
                <span class="ps-field-icon">&#128100;</span>
                <input id="ps-name" type="text" maxlength="24" placeholder="Username" autocomplete="off" spellcheck="false" />
                <span class="ps-field-ok">&#10003;</span>
              </label>
              <label class="ps-field ps-field--area">
                <textarea id="ps-bio" maxlength="280" rows="4" placeholder="Bio — tell the world who you are (optional)"></textarea>
              </label>
            </section>

            <section class="ps-card ps-starters">
              <p class="ps-label">CHOOSE YOUR STARTER</p>
              <div class="ps-starter-list">${starterCards}</div>
            </section>
          </div>

          <button type="button" class="ps-begin" id="ps-begin">BEGIN ADVENTURE&nbsp;&#9654;</button>
        </div>
      </div>`;
  }

  #wireEvents(overlay) {
    overlay.querySelectorAll('[data-avatar]').forEach((btn) => {
      btn.addEventListener('click', () => this.#cycleAvatar(Number(btn.getAttribute('data-avatar'))));
    });
    overlay.querySelectorAll('[data-starter]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.#selectedStarter = Number(btn.getAttribute('data-starter'));
        this.#renderStarters();
      });
    });
    const begin = overlay.querySelector('#ps-begin');
    begin?.addEventListener('click', () => void this.#confirm());
  }

  #cycleAvatar(delta) {
    this.#skinIndex = (this.#skinIndex + delta + SKIN_KEYS.length) % SKIN_KEYS.length;
    this.#renderAvatar();
  }

  /** Render the chosen skin's idle-down cell (top-left 32px of the 4x4 sheet) crisply. */
  #renderAvatar() {
    if (!this.#overlay) return;
    const sprite = /** @type {HTMLElement} */ (this.#overlay.querySelector('#ps-avatar-sprite'));
    if (sprite) {
      sprite.style.backgroundImage = `url('${SKIN_IMG(this.#skinIndex)}')`;
    }
    this.#overlay.querySelectorAll('[data-skin-dot]').forEach((dot) => {
      dot.classList.toggle('ps-dot--on', Number(dot.getAttribute('data-skin-dot')) === this.#skinIndex);
    });
  }

  #renderStarters() {
    if (!this.#overlay) return;
    this.#overlay.querySelectorAll('[data-starter]').forEach((card) => {
      card.classList.toggle('ps-starter--on', Number(card.getAttribute('data-starter')) === this.#selectedStarter);
    });
  }

  #defaultName() {
    const wallet = netManager.wallet;
    return wallet ? `Hunter-${wallet.slice(0, 4)}` : 'Hunter';
  }

  #destroyOverlay() {
    this.#overlay?.remove();
    this.#overlay = undefined;
    this.#nameInput = undefined;
    this.#bioInput = undefined;
    document.getElementById(STYLE_ID)?.remove();
  }

  // ── Confirm: identical data flow to the original scene. ──────────────────────────────────

  async #confirm() {
    if (this.#confirming) return;
    this.#confirming = true;

    const beginBtn = /** @type {HTMLButtonElement} */ (this.#overlay?.querySelector('#ps-begin'));
    if (beginBtn) {
      beginBtn.disabled = true;
      beginBtn.textContent = 'ENTERING…';
    }

    const option = this.#options[this.#selectedStarter];
    const avatar = SKIN_KEYS[this.#skinIndex];
    const username = (this.#nameInput?.value ?? '').trim() || this.#defaultName();
    const bio = (this.#bioInput?.value ?? '').trim();

    const starter = buildStarterMonster(this, option.speciesId);
    if (starter) {
      dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, [starter]);
    }
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_SKIN, avatar);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_AVATAR, avatar);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_NAME, username);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_BIO, bio);
    dataManager.saveData();

    const view = await netManager.createAccount({ username, avatar, bio });
    if (view) {
      applyAccountToDataManager(view);
    }

    this.#destroyOverlay();
    this.scene.start(SCENE_KEYS.WORLD_SCENE);
  }

  // ── Styles ───────────────────────────────────────────────────────────────────────────────

  #injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = this.#css();
    document.head.appendChild(style);
  }

  #css() {
    const font = `${KENNEY_FUTURE_NARROW_FONT_NAME}, system-ui, sans-serif`;
    return `
.ps-overlay{
  position:fixed; inset:0; z-index:1000; pointer-events:auto;
  font-family:${font}; color:#eafff2; -webkit-font-smoothing:antialiased;
  opacity:0; transition:opacity .4s ease;
}
.ps-overlay.ps-in{opacity:1;}
.ps-overlay *{box-sizing:border-box;}

/* Premium emerald background: deep gradient + radial glow + faint dot grid. */
.ps-bg{
  position:absolute; inset:0;
  background:
    radial-gradient(900px 600px at 50% 12%, rgba(52,211,153,0.22), rgba(52,211,153,0) 70%),
    radial-gradient(700px 500px at 80% 90%, rgba(13,42,28,0.9), rgba(13,42,28,0) 70%),
    linear-gradient(160deg, #08130d 0%, #0b2117 45%, #0d2a1c 100%);
}
.ps-bg::after{
  content:""; position:absolute; inset:0; opacity:0.5;
  background-image:radial-gradient(rgba(52,211,153,0.12) 1px, transparent 1px);
  background-size:26px 26px;
  -webkit-mask-image:radial-gradient(120% 90% at 50% 30%, #000 40%, transparent 100%);
  mask-image:radial-gradient(120% 90% at 50% 30%, #000 40%, transparent 100%);
}

.ps-scroll{position:absolute; inset:0; overflow-y:auto; overflow-x:hidden; -webkit-overflow-scrolling:touch;}
.ps-shell{
  position:relative; z-index:1;
  max-width:1180px; margin:0 auto; padding:clamp(20px,4vw,48px) clamp(16px,4vw,40px) 56px;
  min-height:100%; display:flex; flex-direction:column; gap:clamp(20px,3vw,36px);
}

/* Header */
.ps-head{text-align:center; display:flex; flex-direction:column; align-items:center; gap:8px;}
.ps-eyebrow{margin:0; letter-spacing:.32em; font-size:clamp(14px,1.6vw,18px); color:#eafff2; opacity:.9;}
.ps-title{
  margin:0; line-height:.95; font-size:clamp(52px,9vw,92px); letter-spacing:.04em;
  background:linear-gradient(180deg, #aef0c4 0%, #34d399 55%, #15a06a 100%);
  -webkit-background-clip:text; background-clip:text; color:transparent;
  text-shadow:0 0 34px rgba(52,211,153,0.45); -webkit-text-stroke:1px rgba(5,53,31,0.4);
}
.ps-divider{width:12px; height:12px; transform:rotate(45deg); background:#34d399; box-shadow:0 0 14px rgba(52,211,153,.7); border-radius:2px;}
.ps-subtitle{margin:0; font-size:clamp(14px,1.8vw,20px); color:#aef0c4; opacity:.85;}

/* Responsive layout — 3 columns ≥900px, stacked below. */
.ps-grid{
  display:grid; gap:clamp(16px,2vw,24px);
  grid-template-columns:minmax(0,0.9fr) minmax(0,1.05fr) minmax(0,1.1fr);
  align-items:start;
}
@media (max-width:900px){ .ps-grid{grid-template-columns:1fr;} }

/* Glass cards */
.ps-card{
  position:relative; padding:clamp(18px,2.4vw,26px); border-radius:20px;
  background:linear-gradient(180deg, rgba(14,28,20,0.86), rgba(8,18,12,0.82));
  border:1px solid rgba(52,211,153,0.28);
  box-shadow:0 18px 50px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04);
  backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px);
}
.ps-label{margin:0 0 16px; text-align:center; letter-spacing:.22em; font-size:14px; color:#34d399;}

/* Corner accents (avatar card) */
.ps-corner{position:absolute; width:20px; height:20px; border:3px solid rgba(52,211,153,0.85); pointer-events:none;}
.ps-corner--tl{top:10px; left:10px; border-right:0; border-bottom:0;}
.ps-corner--tr{top:10px; right:10px; border-left:0; border-bottom:0;}
.ps-corner--bl{bottom:10px; left:10px; border-right:0; border-top:0;}
.ps-corner--br{bottom:10px; right:10px; border-left:0; border-top:0;}

/* Avatar */
.ps-avatar-stage{display:flex; align-items:center; justify-content:center; gap:clamp(10px,2vw,18px);}
.ps-avatar-frame{position:relative; width:140px; height:152px; display:flex; align-items:flex-end; justify-content:center;}
.ps-avatar-sprite{
  width:128px; height:128px; position:relative; z-index:1;
  background-repeat:no-repeat; background-position:0 0; background-size:400% 400%;
  image-rendering:pixelated; image-rendering:crisp-edges;
  filter:drop-shadow(0 6px 10px rgba(0,0,0,0.5));
}
.ps-podium{
  position:absolute; bottom:6px; left:50%; transform:translateX(-50%);
  width:120px; height:30px; border-radius:50%;
  background:radial-gradient(closest-side, rgba(52,211,153,0.18), rgba(10,20,16,0.85));
  border:2px solid rgba(44,74,56,0.9); z-index:0;
}
.ps-arrow{
  flex:0 0 auto; width:44px; height:44px; border-radius:50%; cursor:pointer;
  background:rgba(16,35,26,0.92); color:#aef0c4; border:2px solid rgba(52,211,153,0.8);
  font-size:18px; display:flex; align-items:center; justify-content:center;
  transition:transform .12s ease, color .12s ease, border-color .12s ease;
}
.ps-arrow:hover{color:#f5b23e; border-color:#f5b23e;}
.ps-arrow:active{transform:scale(0.9);}
.ps-dots{display:flex; justify-content:center; gap:9px; margin-top:18px;}
.ps-dot{width:8px; height:8px; border-radius:50%; background:rgba(159,196,173,0.4); transition:all .15s ease;}
.ps-dot--on{background:#34d399; box-shadow:0 0 8px rgba(52,211,153,.8); transform:scale(1.25);}

/* Identity fields */
.ps-field{
  display:flex; align-items:center; gap:10px; margin-top:14px; padding:12px 16px; border-radius:14px;
  background:rgba(8,18,12,0.7); border:1px solid rgba(52,211,153,0.35);
  transition:border-color .15s ease, box-shadow .15s ease;
}
.ps-field:focus-within{border-color:#34d399; box-shadow:0 0 0 3px rgba(52,211,153,0.22);}
.ps-field--area{align-items:flex-start;}
.ps-field-icon{opacity:.7; font-size:16px;}
.ps-field-ok{color:#34d399; opacity:.7; font-size:14px;}
.ps-field input, .ps-field textarea{
  flex:1; min-width:0; width:100%; background:transparent; border:0; outline:none;
  color:#eafff2; font-family:${font}; font-size:17px; resize:none; line-height:1.4;
}
.ps-field textarea{height:96px;}
.ps-field input::placeholder, .ps-field textarea::placeholder{color:rgba(159,196,173,0.6);}

/* Starter cards */
.ps-starter-list{display:flex; flex-direction:column; gap:12px;}
.ps-starter{
  display:flex; align-items:center; gap:14px; width:100%; cursor:pointer; text-align:left;
  padding:12px 16px; border-radius:16px;
  background:rgba(8,18,12,0.7); border:2px solid rgba(44,74,56,0.9); color:inherit;
  font-family:${font}; transition:transform .14s ease, border-color .14s ease, box-shadow .14s ease, background .14s ease;
}
.ps-starter:hover{background:rgba(12,26,18,0.85);}
.ps-starter--on{
  border-color:var(--elem); transform:scale(1.04);
  box-shadow:0 0 0 1px var(--elem), 0 0 26px color-mix(in srgb, var(--elem) 45%, transparent);
  background:rgba(12,28,20,0.92);
}
.ps-starter-art{flex:0 0 auto; width:64px; height:64px; display:flex; align-items:center; justify-content:center;}
.ps-starter-sprite{width:64px; height:64px; object-fit:contain; image-rendering:pixelated; image-rendering:crisp-edges;}
.ps-starter-sprite--fallback{font-size:40px; line-height:64px; text-align:center;}
.ps-starter-meta{display:flex; flex-direction:column; gap:4px; flex:1; min-width:0;}
.ps-starter-name{font-size:22px; color:#eafff2;}
.ps-starter-elem{font-size:14px; letter-spacing:.12em;}
.ps-gem{flex:0 0 auto; width:18px; height:18px; transform:rotate(45deg); border:2px solid rgba(255,255,255,0.5); border-radius:3px;}

/* Begin button */
.ps-begin{
  align-self:center; margin-top:6px; width:min(420px,100%); padding:18px 24px; cursor:pointer;
  border-radius:16px; border:2px solid #8df0b4; color:#03210f; font-family:${font};
  font-size:24px; letter-spacing:.06em;
  background:linear-gradient(180deg, #34d399 0%, #18b85f 100%);
  box-shadow:0 12px 36px rgba(24,184,95,0.45), 0 0 22px rgba(52,211,153,0.5);
  transition:transform .12s ease, filter .12s ease;
}
.ps-begin:hover{filter:brightness(1.08);}
.ps-begin:active{transform:scale(0.98);}
.ps-begin:disabled{filter:grayscale(.3) brightness(.85); cursor:default;}

@media (max-width:900px){
  .ps-shell{padding-bottom:40px;}
  .ps-begin{width:100%;}
}
`;
  }
}
